import React, { useState } from 'react';
import { CMSFieldDef } from '../services/api';
import RichTextEditor from './RichTextEditor';

interface ContentBlockEditorProps {
  blockId: number;
  blockFields: CMSFieldDef[];
  values: Record<string, any>;
  onSave: (blockId: number, fields: Record<string, any>) => Promise<void>;
  onDelete: (blockId: number) => Promise<void>;
  onMoveUp: (blockId: number) => Promise<void>;
  onMoveDown: (blockId: number) => Promise<void>;
  isFirst: boolean;
  isLast: boolean;
}

const ContentBlockEditor: React.FC<ContentBlockEditorProps> = ({
  blockId,
  blockFields,
  values,
  onSave,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}) => {
  const [localValues, setLocalValues] = useState<Record<string, any>>({ ...values });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleChange = (name: string, value: string) => {
    setLocalValues((prev) => ({ ...prev, [name]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(blockId, localValues);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      alert('Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this block?')) return;
    try {
      await onDelete(blockId);
    } catch (err) {
      alert('Delete failed. Please try again.');
    }
  };

  return (
    <div style={{
      border: '1px solid #ddd',
      borderRadius: '6px',
      padding: '1rem',
      marginBottom: '0.75rem',
      backgroundColor: '#fafafa',
    }}>
      {blockFields.map((field) => (
        <div key={field.name} style={{ marginBottom: '0.75rem' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', color: '#555' }}>
            {field.label}
          </label>
          {field.type === 'textarea' ? (
            <RichTextEditor
              key={`block-${blockId}-${field.name}`}
              content={localValues[field.name] ?? ''}
              onChange={(html) => handleChange(field.name, html)}
              minHeight={120}
            />
          ) : (
            <input
              type={field.type}
              value={localValues[field.name] ?? ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              style={{ width: '100%', padding: '0.4rem', fontSize: '0.875rem', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box' }}
            />
          )}
        </div>
      ))}

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem', backgroundColor: saved ? '#28a745' : '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
        >
          {saving ? 'Saving…' : saved ? 'Saved' : 'Save'}
        </button>
        <button
          onClick={handleDelete}
          style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
        >
          Delete
        </button>
        <button
          onClick={() => onMoveUp(blockId)}
          disabled={isFirst}
          style={{ padding: '0.35rem 0.5rem', fontSize: '0.8rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: isFirst ? 'not-allowed' : 'pointer', opacity: isFirst ? 0.5 : 1 }}
        >
          ↑
        </button>
        <button
          onClick={() => onMoveDown(blockId)}
          disabled={isLast}
          style={{ padding: '0.35rem 0.5rem', fontSize: '0.8rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: isLast ? 'not-allowed' : 'pointer', opacity: isLast ? 0.5 : 1 }}
        >
          ↓
        </button>
      </div>
    </div>
  );
};

export default ContentBlockEditor;
